import { Box, Grid, Paper, styled } from '@mui/material';
import { mat3 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { initializeEngine } from '~/engine/Engine';
import { Environment } from '~/engine/data/Environment';
import { ToolManager } from '~/engine/tool/ToolManager';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    upperSideViewState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import {
    brushOptionsState,
    lineOptionsState,
    mainViewWidthState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import {
    expandDoseStatisticsTableState,
    showDoseStatisticsTableFullScreenState,
    showDoseStatisticsTableState,
    showDVHViewComponentFullScreenState,
    showDVHViewComponentState,
    showFullscreenState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';
import DVHLineChartComponent from '../dvh-line-chart';
import TablePanel from '../table-panel';
import Viewport from './Viewport';

const ViewportLayoutContainer = styled(Box)`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    height: 100%;
`;

const getPlaneViewOrientationMatrix = (viewportOrientation: string): mat3 => {
    return viewportOrientation === Orientations.TRANSVERSAL_ORIENTATION
        ? PlaneView.TRANSVERSAL_ORIENTATION
        : viewportOrientation === Orientations.SAGITTAL_ORIENTATION
          ? PlaneView.SAGITTAL_ORIENTATION
          : PlaneView.CORONAL_ORIENTATION;
};

const getInvertedPlaneViewOrientationMatrix = (
    viewportOrientation: string
): mat3 => {
    return viewportOrientation === Orientations.TRANSVERSAL_ORIENTATION
        ? PlaneView.TRANSVERSAL_ORIENTATION_INVERTED
        : viewportOrientation === Orientations.SAGITTAL_ORIENTATION
          ? PlaneView.SAGITTAL_ORIENTATION_INVERTED
          : PlaneView.CORONAL_ORIENTATION_INVERTED;
};

const convertMainViewWidthFromPercentToGridValue = (
    mainViewWidthPrecent: number
): number => (Math.max(0, Math.min(100, mainViewWidthPrecent)) / 100) * 12;

/**
 * The main layout component for the viewer, which contains the 3 main viewports and the DVH and dose statistics components.
 */
const Layout = () => {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const currentDose = useRecoilValue(currentDoseState);

    const [environment, setEnvironment] = useRecoilState(environmentState);
    const [viewManager, setViewManager] = useRecoilState(viewManagerState);
    const [primaryImage] = useRecoilState(primaryImageState);

    const viewportLayoutContainer = useRef<HTMLElement>(null);
    const [showFullscreen] = useRecoilState(showFullscreenState);
    const [showDVHViewComponentFullScreen, setShowDVHViewComponentFullScreen] =
        useRecoilState(showDVHViewComponentFullScreenState);
    const [showDVHViewComponent, setShowDVHViewComponent] = useRecoilState(
        showDVHViewComponentState
    );
    const [showDoseStatisticsTable, setShowDoseStatisticsTable] =
        useRecoilState(showDoseStatisticsTableState);
    const [expandDoseStatisticsTable, setExpandDoseStatisticsTable] =
        useRecoilState(expandDoseStatisticsTableState);
    const [
        showDoseStatisticsTableFullScreen,
        setShowDoseStatisticsTableFullScreen,
    ] = useRecoilState(showDoseStatisticsTableFullScreenState);
    const viewportOrientationOrder = useRecoilValue(
        viewportOrientationOrderState
    );
    const mainViewWidth = useRecoilValue(mainViewWidthState);

    const mainCanvas = useRef<HTMLCanvasElement | null>(null);
    const upperSideCanvas = useRef<HTMLCanvasElement | null>(null);
    const lowerSideCanvas = useRef<HTMLCanvasElement | null>(null);
    const mainCanvas2D = useRef<HTMLCanvasElement | null>(null);
    const upperSideCanvas2D = useRef<HTMLCanvasElement | null>(null);
    const lowerSideCanvas2D = useRef<HTMLCanvasElement | null>(null);

    const selectedTool = useRecoilValue(selectedToolState);
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const lineOptions = useRecoilValue(lineOptionsState);
    const [selectedROI] = useRecoilState(selectedROIState);

    const [mainView, setMainView] = useRecoilState(mainViewState);
    const [upperSideView, setUpperSideView] =
        useRecoilState(upperSideViewState);
    const [lowerSideView, setLowerSideView] =
        useRecoilState(lowerSideViewState);

    const doseStatisticsRef = useRef<HTMLTableSectionElement | null>(null);

    /**
     * Called on component mount, and initializes the {@link Environment} with all of the patient's data.
     */
    useEffect(() => {
        initializeEnvironment();
    }, []);

    /**
     * setting up the {@link PlaneView} object after the {@link Environment} has been set up
     */
    useEffect(() => {
        if (environment) {
            setViews(
                environment,
                mainCanvas,
                upperSideCanvas,
                lowerSideCanvas,
                mainCanvas2D,
                upperSideCanvas2D,
                lowerSideCanvas2D
            );
        }
    }, [environment]);

    /**
     * reconfiguring the {@link PlaneView} if the uploaded image is in HFP orientation
     */
    useEffect(() => {
        if (primaryImage) {
            const isPatientOrientationHFP =
                primaryImage.firstSlice.headFirstProne;
            if (isPatientOrientationHFP) {
                setViewsOrientationHFP();
            } else {
                setViewsOrientation();
            }
        }
    }, [primaryImage]);

    /**
     * handling the viewport orientation change by setting up new orientation matrix and updating the view
     */
    useEffect(() => {
        handleViewportOrientationChange();
    }, [viewportOrientationOrder]);

    useEffect(() => {
        handleShowDVHViewComponent();
    }, [currentDose, showDVHViewComponent]);

    useEffect(() => {
        setSelectedTool();
    }, [
        selectedTool,
        brushOptions.mode,
        brushOptions.erase,
        lineOptions,
        viewManager,
        environment?.image,
        selectedROI,
        viewportOrientationOrder,
    ]);

    const initializeEnvironment = () => {
        if (
            !viewportLayoutContainer.current ||
            !mainCanvas.current ||
            !upperSideCanvas.current ||
            !lowerSideCanvas.current
        ) {
            throw new Error(
                `Viewport inizitalization failed as not all viewports had canvases available`
            );
        }
        if (!dicomProcessor) {
            throw new Error(`DicomProcessor not in Context`);
        }
        if (!patientRepository) {
            throw new Error(`PatientRepository not in Context`);
        }

        const viewManager = initializeEngine(
            dicomProcessor,
            viewportLayoutContainer.current
        );
        setEnvironment(viewManager.env);
        setViewManager(viewManager);
    };

    const setViews = (
        env: Environment,
        mainCanvas: React.RefObject<HTMLCanvasElement | null>,
        upperSideCanvas: React.RefObject<HTMLCanvasElement | null>,
        lowerSideCanvas: React.RefObject<HTMLCanvasElement | null>,
        mainCanvas2D: React.RefObject<HTMLCanvasElement | null>,
        upperSideCanvas2D: React.RefObject<HTMLCanvasElement | null>,
        lowerSideCanvas2D: React.RefObject<HTMLCanvasElement | null>
    ) => {
        if (
            mainCanvas.current &&
            upperSideCanvas.current &&
            lowerSideCanvas.current &&
            viewManager &&
            mainCanvas2D.current &&
            upperSideCanvas2D.current &&
            lowerSideCanvas2D.current
        ) {
            const main = new PlaneView(
                env,
                viewManager,
                PlaneView.ORIENTATIONS[viewportOrientationOrder[0]],
                mainCanvas.current,
                mainCanvas2D.current,
                viewportOrientationOrder[0]
            );
            setMainView(main);
            viewManager.addView(main);

            const upper = new PlaneView(
                env,
                viewManager,
                PlaneView.ORIENTATIONS[viewportOrientationOrder[1]],
                upperSideCanvas.current,
                upperSideCanvas2D.current,
                viewportOrientationOrder[1]
            );
            setUpperSideView(upper);
            viewManager.addView(upper);

            const lower = new PlaneView(
                env,
                viewManager,
                PlaneView.ORIENTATIONS[viewportOrientationOrder[2]],
                lowerSideCanvas.current,
                lowerSideCanvas2D.current,
                viewportOrientationOrder[2]
            );
            setLowerSideView(lower);
            viewManager.addView(lower);
        }
    };

    const setViewsOrientation = () => {
        if (mainView && upperSideView && lowerSideView) {
            const views = [mainView, upperSideView, lowerSideView];
            views.forEach((view, index) => {
                view.setOrientation(
                    PlaneView.ORIENTATIONS[viewportOrientationOrder[index]]
                );
                view.updateView();
            });
        }
    };

    const setViewsOrientationHFP = () => {
        if (mainView && upperSideView && lowerSideView) {
            const views = [mainView, upperSideView, lowerSideView];
            views.forEach((view, index) => {
                view.setOrientation(
                    PlaneView.INVERTED_ORIENTATIONS[
                        viewportOrientationOrder[index]
                    ]
                );
                view.updateView();
            });
        }
    };

    const handleShowDVHViewComponent = () => {
        if (currentDose) {
            if (showDVHViewComponent === null) {
                setShowDVHViewComponent(false);
                setShowDoseStatisticsTable(false);
                setExpandDoseStatisticsTable(false);
                setShowDoseStatisticsTableFullScreen(false);
            } else if (showDVHViewComponent === false) {
                setShowDVHViewComponentFullScreen(false);
                setShowDoseStatisticsTable(null);
                setExpandDoseStatisticsTable(null);
                setShowDoseStatisticsTableFullScreen(null);
            }
        } else if (showDVHViewComponent !== null) {
            setShowDVHViewComponent(null);
            setShowDoseStatisticsTable(null);
            setExpandDoseStatisticsTable(null);
            setShowDoseStatisticsTableFullScreen(null);
            setShowDVHViewComponentFullScreen(false);
        }
    };

    const handleDoseStatisticsTableScroll = (
        e: React.WheelEvent<HTMLDivElement>
    ) => {
        if (doseStatisticsRef.current) {
            doseStatisticsRef.current.scrollTop += e.deltaY / 4;
        }
    };

    const handleViewportOrientationChange = (): void => {
        if (mainView && upperSideView && lowerSideView) {
            const isPatientOrientationHFP =
                primaryImage?.firstSlice.headFirstProne;
            viewportOrientationOrder
                .map((viewportOrientation) =>
                    isPatientOrientationHFP
                        ? getInvertedPlaneViewOrientationMatrix(
                              viewportOrientation
                          )
                        : getPlaneViewOrientationMatrix(viewportOrientation)
                )
                .forEach((newViewOrientationMatrix, index) => {
                    const view =
                        index === 0
                            ? mainView
                            : index === 1
                              ? upperSideView
                              : lowerSideView;
                    const newOrientation = viewportOrientationOrder[index];
                    view.setOrientation(
                        newViewOrientationMatrix,
                        newOrientation
                    );
                    view.updateView();
                });
        }
    };

    const updateBrushSizeCallback = (diameter: number) => {
        setBrushOptions((options) => ({
            ...options,
            diameter,
        }));
    };

    const setSelectedTool = () => {
        if (!viewManager || !environment || !environment.image) return;
        const views = [mainView, upperSideView, lowerSideView];
        const transversalView =
            views[
                viewportOrientationOrder.findIndex(
                    (orientation) =>
                        orientation === Orientations.TRANSVERSAL_ORIENTATION
                )
            ];

        if (!transversalView) return;

        const { image, panZoom, structureSet } = environment;
        const structure = structureSet?.getRoi(selectedROI);

        const contouringToolOptions =
            selectedTool === 'brush'
                ? brushOptions
                : selectedTool === 'line'
                  ? lineOptions
                  : undefined;

        ToolManager.setActiveTool(
            selectedTool,
            structure,
            image,
            panZoom,
            transversalView,
            contouringToolOptions,
            updateBrushSizeCallback,
            viewManager
        );
    };

    return (
        <ViewportLayoutContainer ref={viewportLayoutContainer}>
            <Grid
                container
                spacing={1}
                p={1}
                position="relative"
                overflow="hidden"
                sx={{
                    height: expandDoseStatisticsTable
                        ? 'calc(100% - 55px)'
                        : 'auto',
                    position: showDoseStatisticsTableFullScreen
                        ? 'absolute'
                        : 'static',
                    left: showDoseStatisticsTableFullScreen ? '-100%' : '',
                }}
            >
                <Grid
                    item
                    xs={
                        showFullscreen
                            ? 12
                            : convertMainViewWidthFromPercentToGridValue(
                                  mainViewWidth
                              )
                    }
                    position={
                        showDVHViewComponentFullScreen ? 'absolute' : 'static'
                    }
                    left={showDVHViewComponentFullScreen ? '-100%' : ''}
                >
                    <Paper
                        sx={{
                            background: 'black',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        <Viewport
                            view={mainView}
                            viewportOrientation={viewportOrientationOrder[0]}
                            canvas={mainCanvas}
                            canvas2D={mainCanvas2D}
                        />
                    </Paper>
                </Grid>
                <Grid
                    item
                    xs={
                        showDVHViewComponentFullScreen
                            ? 12
                            : 12 -
                              convertMainViewWidthFromPercentToGridValue(
                                  mainViewWidth
                              )
                    }
                    container
                    spacing={1}
                    direction={
                        showDVHViewComponent || showDoseStatisticsTable
                            ? 'column'
                            : 'row'
                    }
                    position={showFullscreen ? 'absolute' : 'relative'}
                    left={showFullscreen ? '-100%' : ''}
                    data-testid="side-viewports"
                >
                    {showDVHViewComponent && currentDose ? (
                        <Grid
                            item
                            xs={12}
                            md={showDVHViewComponentFullScreen ? 12 : 6}
                        >
                            <Paper
                                sx={{
                                    background: 'black',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <DVHLineChartComponent />
                            </Paper>
                        </Grid>
                    ) : null}
                    <Grid
                        item
                        container
                        spacing={1}
                        md={showDVHViewComponent ? 6 : 12}
                        position={
                            showDVHViewComponentFullScreen
                                ? 'absolute'
                                : 'relative'
                        }
                        left={showDVHViewComponentFullScreen ? '-100%' : ''}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={showDVHViewComponent ? 3 : 12}
                            md={
                                showDVHViewComponent || showDoseStatisticsTable
                                    ? 6
                                    : 12
                            }
                        >
                            <Paper
                                sx={{
                                    background: 'black',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Viewport
                                    view={upperSideView}
                                    viewportOrientation={
                                        viewportOrientationOrder[1]
                                    }
                                    canvas={upperSideCanvas}
                                    canvas2D={upperSideCanvas2D}
                                />
                            </Paper>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={showDVHViewComponent ? 3 : 12}
                            md={
                                showDVHViewComponent || showDoseStatisticsTable
                                    ? 6
                                    : 12
                            }
                        >
                            <Paper
                                sx={{
                                    background: 'black',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Viewport
                                    view={lowerSideView}
                                    viewportOrientation={
                                        viewportOrientationOrder[2]
                                    }
                                    canvas={lowerSideCanvas}
                                    canvas2D={lowerSideCanvas2D}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {showDoseStatisticsTable && currentDose ? (
                <Grid
                    container
                    item
                    position="relative"
                    sx={{
                        overflow: expandDoseStatisticsTable ? 'hidden' : 'auto',
                        height: expandDoseStatisticsTable ? '55px' : 'auto',
                    }}
                    ref={doseStatisticsRef}
                    onWheel={
                        expandDoseStatisticsTable
                            ? () => {}
                            : handleDoseStatisticsTableScroll
                    }
                >
                    <TablePanel />
                </Grid>
            ) : null}
        </ViewportLayoutContainer>
    );
};

export default Layout;
